import { useMemo } from 'react';

import { useAppSelector } from '../lib/reduxHooks.js';
import { selectPermissions, selectAuthStatus } from '../store/slices/auth.js';

/**
 * Hook to check the current user's role-based permissions.
 *
 * Resolves the CURRENT user's permissions from the AUTH SESSION — the Redux
 * auth slice's `permissions[]`, populated by `/auth/me`. This is the exact
 * permission-name list the server grants the signed-in user (union of every
 * assigned role's permissions), computed server-side.
 *
 * We deliberately do NOT read the `role_permissions` / `roles` / `permissions`
 * tables client-side. Those list endpoints are admin-gated: once RBAC
 * enforcement is on they return 403 for non-admins, which used to leave this
 * hook unable to resolve permissions and made `useOfficePermissions` (and the
 * sidebar/button gating built on it) fail open — showing the whole office app
 * to users who lack the grants. Sourcing from `/auth/me` (which every
 * authenticated user can read) fixes that fail-open. This mirrors the field
 * app's `usePagePermission`, which already reads `selectPermissions`.
 *
 * @example
 * ```tsx
 * const { hasPermission, isLoading } = useUserPermissions();
 * if (hasPermission('view_estimation')) { ... }
 * ```
 */
export function useUserPermissions() {
  const permissions = useAppSelector(selectPermissions);
  const authStatus = useAppSelector(selectAuthStatus);

  // Memoized Set of permission names for O(1) lookups.
  const permissionNames = useMemo(
    () => new Set<string>(permissions),
    [permissions]
  );

  const hasPermission = useMemo(
    () => (permName: string) => permissionNames.has(permName),
    [permissionNames]
  );

  return {
    hasPermission,
    permissions: permissionNames,
    // Loading reflects the auth-load state, not a role-table fetch. Only the
    // initial `'loading'` (or pre-load `'idle'`) counts as loading; once auth
    // has resolved (`'succeeded'`/`'failed'`) the permission answer is final.
    isLoading: authStatus === 'loading',
  };
}
